<script setup lang="ts">
import type { ActiveFilter } from "../../types/TableFilter.types";
import { computed, ref, useId } from "vue";
import { useI18n } from "vue-i18n";
import { defaultConditionOf, isFilterSet, useTableFilters } from "../../composables/Table/useTableFilters";
import { TableFilterType } from "../../types/TableFilter.types";
import VButton from "../Button/VButton.vue";
import VTableFilterEditor from "./VTableFilterEditor.vue";

defineOptions({
  name: "VTableFilterDropdown",
});

/**
 * One filter in a popover: the filter of a column header or a filter button in the toolbar. A choice (list,
 * single choice, yes or no) applies at once; text, numbers and dates apply with Toepassen.
 */
const { field } = defineProps<{
  field: string
}>();

const emit = defineEmits<{
  close: []
}>();

type FilterDraft = Pick<ActiveFilter, "condition" | "value">;

const { t } = useI18n();
const { getConfig, getFilterForField, addFilter, removeFilter } = useTableFilters();
const labelId = `${useId()}-label`;

const config = computed(() => {
  const found = getConfig(field);
  if (!found) {
    throw new Error(`VTableFilterDropdown: no filterable field "${field}" was provided.`);
  }
  return found;
});

const INSTANT_TYPES = [TableFilterType.SELECT, TableFilterType.MULTI_SELECT, TableFilterType.BOOLEAN];
const instant = computed(() => INSTANT_TYPES.includes(config.value.type));

function fromActive(): FilterDraft {
  const active = getFilterForField(field);
  if (!active) {
    return { condition: defaultConditionOf(config.value), value: null };
  }
  return { condition: active.condition, value: Array.isArray(active.value) ? [...active.value] as ActiveFilter["value"] : active.value };
}

const localDraft = ref<FilterDraft>(fromActive());

function commit(next: FilterDraft) {
  if (isFilterSet(next)) {
    addFilter({ field, ...next });
    return;
  }
  removeFilter(field);
}

const draft = computed<FilterDraft>({
  get: () => localDraft.value,
  set: (next) => {
    localDraft.value = next;
    if (instant.value) {
      commit(next);
    }
  },
});

const hasActive = computed(() => !!getFilterForField(field));

function clear() {
  removeFilter(field);
  localDraft.value = { condition: defaultConditionOf(config.value), value: null };
}

function apply() {
  commit(localDraft.value);
  emit("close");
}
</script>

<template>
  <div class="flex w-[300px] max-w-[calc(100vw-32px)] flex-col gap-2 p-3" data-testid="table-filter-dropdown" @keydown.esc.stop="emit('close')">
    <div class="flex items-center justify-between gap-2">
      <div :id="labelId" class="text-sm font-medium text-gray-800 dark:text-gray-200">
        {{ config.label }}
      </div>
      <button
        v-if="hasActive"
        type="button"
        class="h-7 cursor-pointer rounded-full px-2 text-xs font-medium text-danger-700 hover:bg-gray-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-danger-400 dark:hover:bg-dark-700"
        @click="clear"
      >
        {{ t("filters.clear") }}
      </button>
    </div>

    <VTableFilterEditor v-model="draft" :config="config" :label-id="labelId" />

    <div v-if="!instant" class="mt-1 flex justify-end gap-2">
      <VButton variant="gray" size="small" rounded @click="emit('close')">
        {{ t("filters.cancel") }}
      </VButton>
      <VButton size="small" rounded @click="apply">
        {{ t("filters.apply") }}
      </VButton>
    </div>
  </div>
</template>
